import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Field,
  FieldTypes,
  Hint,
  InfoOutlineIcon,
  Inscription,
  LabelWithSubfield,
  Text,
} from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { BOOLEAN_STRINGS, MUST_BE_GREATER_THAN_ZERO_TEXT, NO_KEY, REQUIRED_TEXT } from '@/common/constants';
import { FALSE_KEY, TRUE_KEY, YES_KEY } from '@/common/constants.ts';
import useAccordionStore from '@/components/elements/Accordion/useAccordion.store';
import { PLAN_MODULES } from '@/modules/plan/plan.constants.ts';
import { aiAdjudicationType, ReturnFundsAfterClosureType } from '@/modules/plan/plan.types';
import { useAccountFundingParamsStore } from '@/modules/plan/PlanSetup/AccountFunding/AccountFundingParams';
import { usePlanFieldsState, useTemplateFieldState } from '@/modules/plan/PlanSetup/hooks';
import { OmnibusKeys } from '@/modules/plan/PlanSetup/PlanSetup.constants';
import { PLAN_SETUP_FIELD } from '@/modules/plan/PlanSetup/PlanSetup.constants';
import { scrollToSetupSection } from '@/modules/plan/PlanSetup/Sidebar/hooks/scrollToSetupSection';
import { usePreviewStore } from '@/modules/plan/PlanSetup/stores';
import useFieldsWithDefaultValues from '@/utils/hooks/useFieldsWithDefaultValues';

import { useIRSMaximumQuery } from '../queries';
import { useIRSMaximumTemplateTypesQuery } from '../queries';
import { useAdditionalStore } from '../stores';
const VALIDATOR = yup.string().nullable();
const REQUIRED_VALIDATOR = yup.string().trim().nullable().required(REQUIRED_TEXT);
const ELECTION_AMOUNT_TEXT = 'Maximum election amount should be bigger than Minimum election amount';

const CENTLESS_KEY = '.00';
const IRS_MAXIMUM_KEY = 'IRS_LIMIT';
const CUSTOM_KEY = 'CUSTOM';
const ALLOW_INVESTMENTS_KEY = 'allowInvestments';
const ELECTION_BASED_KEY = 'ELECTION_BASED';
const GREATER_TEXT = 'The value must be greater than 0';
const CURRENCY_KEY = '$';
const parseCost = (value = '') => {
  const cost = parseFloat(value.replace(CURRENCY_KEY, ''));
  if (_.isNaN(cost)) {
    return 0;
  }
  return cost;
};

const SPEND_LIMIT_IRS = 'IRS_LIMIT';
export default (
  isActivationMode?: boolean,
  isTemplateMode?: boolean,
  isEditMode?: boolean,
  isPlanActive?: boolean
) => {
  const { handleAdd: handleAccordionAdd } = useAccordionStore();
  const handleOpenEligibleServices = useCallback(() => {
    handleAccordionAdd({ id: PLAN_MODULES.ELIGIBLE_SERVICES, isExpanded: true });
    scrollToSetupSection(PLAN_MODULES.ELIGIBLE_SERVICES, { behavior: 'smooth' });
  }, [handleAccordionAdd]);
  const errorFields = useAdditionalStore((state) => state.errorFields);
  const sourceState = useAdditionalStore((state) => state.sourceState);
  const currentState = useAdditionalStore((state) => state.state);
  const allowInvestments = useAdditionalStore((state) => state.state.allowInvestments);
  const isAllowInvestments = useMemo(() => {
    if (allowInvestments === TRUE_KEY) return true;
    return false;
  }, [allowInvestments]);

  const isDisplayedPendContribution = useAdditionalStore((state) => state.state.CIPRequired);
  const totalPlanLimit = useAdditionalStore((state) => state.state.totalPlanLimit);
  const perPersonSpendLimit = useAdditionalStore((state) => state.state.perPersonSpendLimit);
  const perCategorySpendLimit = useAdditionalStore((state) => state.state.perCategorySpendLimit);

  const isAnySpendLimitSelected = useMemo(() => {
    const isTotalPlanLimitSelected = totalPlanLimit === true;
    const isPerPersonSelected = !!perPersonSpendLimit && perPersonSpendLimit !== NO_KEY;
    const isPerCategorySelected = perCategorySpendLimit === true;

    return isTotalPlanLimitSelected || isPerPersonSelected || isPerCategorySelected;
  }, [totalPlanLimit, perPersonSpendLimit, perCategorySpendLimit]);

  const previewMode = usePreviewStore((state) => state.previewMode);
  const appliedSourceState = useMemo(() => {
    if (previewMode) {
      return currentState;
    }
    return sourceState;
  }, [currentState, previewMode, sourceState]);

  const isReplenished = useAccountFundingParamsStore((state) => state.state.isReplenished);
  const omnibus = useAccountFundingParamsStore((state) => state.state.omnibus);

  const isHSAOmnibus = useMemo(() => omnibus === OmnibusKeys.HSA, [omnibus]);
  // const allowInvestments = useAdditionalStore((state) => state.state.allowInvestments);
  // It's needed for sync checkboxes
  // @ts-ignore

  const { data: planIRSOptions } = useIRSMaximumQuery(isTemplateMode);
  const { data: templateIRSOptions } = useIRSMaximumTemplateTypesQuery(isTemplateMode);

  const IRSOptions = useMemo(() => {
    if (isTemplateMode) {
      return templateIRSOptions;
    }
    return planIRSOptions;
  }, [isTemplateMode, planIRSOptions, templateIRSOptions]);

  const onlyActivationRequired = useMemo(
    () => (isActivationMode ? REQUIRED_VALIDATOR : VALIDATOR),
    [isActivationMode]
  );
  const fields: Field[] = useMemo(
    () => [
      {
        name: 'availableBalance',
        type: FieldTypes.Radio,
        showRequireIcon: true,
        label: 'Available balance',
        options: [
          {
            label: 'Actual balance',
            value: 'ACTUAL_BALANCE',
          },
          {
            label: 'Election based',
            value: ELECTION_BASED_KEY,
          },
        ],
        validator: isReplenished
          ? onlyActivationRequired
          : onlyActivationRequired
              .test({
                test: (val) => val !== ELECTION_BASED_KEY,
                message: 'Election based option cannot be set if replenishment is not allowed',
              })
              .nullable(),
      },

      {
        name: 'maximumElectionAmount',
        type: FieldTypes.Radio,
        expandItems: true,
        showRequireIcon: true,
        label: 'Maximum election amount',
        options: [
          {
            label: 'Unlimited',
            value: 'UNLIMITED',
          },
          {
            label: 'IRS limit',
            value: IRS_MAXIMUM_KEY,
          },
          {
            label: 'Custom',
            value: CUSTOM_KEY,
          },
          // {
          //   label: 'Comute',
          //   value: 'maximumElectionAmount.commute',
          // },
        ],
        validator: onlyActivationRequired,
        subFields: [
          {
            name: 'maximumElectionAmountCustom',
            parentValue: CUSTOM_KEY,
            type: FieldTypes.Currency,
            value: '',
            label: '',
            placeholder: 'Enter $ amount',
            validator: yup
              .string()
              .when('maximumElectionAmount', {
                is: (val: string) => val === CUSTOM_KEY,
                then: (schema) => VALIDATORS.GET_REQUIRED_SCHEMA(schema),
                otherwise: () => VALIDATOR,
              })
              .test({
                exclusive: false,
                test(val) {
                  const { minimumElectionAmountCustom, maximumElectionAmount } = this.parent;
                  if (maximumElectionAmount !== CUSTOM_KEY) {
                    return true;
                  }
                  if (!val || !_.toNumber(minimumElectionAmountCustom)) {
                    if (_.toNumber(minimumElectionAmountCustom) !== 0 && _.toNumber(val) !== 0) {
                      return true;
                    }
                  }
                  return _.toNumber(minimumElectionAmountCustom) < _.toNumber(val);
                },
                message: ELECTION_AMOUNT_TEXT,
              }),
          },
          {
            name: 'maximumElectionAmountIRS',
            parentValue: IRS_MAXIMUM_KEY,
            type: FieldTypes.Dropdown,
            singleMode: true,
            placeholder: 'Select max value',
            options: IRSOptions,
            validator: yup.string().when('maximumElectionAmount', {
              is: (val: string) => val === IRS_MAXIMUM_KEY,
              then: (schema) => VALIDATORS.GET_REQUIRED_SCHEMA(schema),
              otherwise: (schema) => schema.nullable(),
            }),
            label: '',
          },
        ],
      },
      {
        name: 'minimumElectionAmount',
        type: FieldTypes.Radio,
        expandItems: true,
        showRequireIcon: true,
        label: 'Minimum election amount',
        options: [
          {
            label: 'Zero',
            value: 'ZERO',
          },
          {
            label: 'Custom',
            value: CUSTOM_KEY,
          },
        ],
        validator: isActivationMode ? REQUIRED_VALIDATOR : VALIDATOR,
        subFields: [
          {
            name: 'minimumElectionAmountCustom',
            parentValue: CUSTOM_KEY,
            type: FieldTypes.Currency,
            value: '',
            label: '',
            placeholder: 'Enter $ amount',
            validator: yup
              .string()
              .when('minimumElectionAmount', {
                is: (val: string) => val === CUSTOM_KEY,
                then: (schema) => VALIDATORS.GET_REQUIRED_SCHEMA(schema),
                otherwise: () => yup.string().nullable(),
              })
              .test({
                exclusive: false,
                test(val) {
                  const {
                    maximumElectionAmountCustom,
                    maximumElectionAmount,
                    minimumElectionAmount,
                    maximumElectionAmountIRS,
                  } = this.parent;

                  if (minimumElectionAmount !== CUSTOM_KEY) {
                    return true;
                  }
                  if (maximumElectionAmount === IRS_MAXIMUM_KEY) {
                    const foundMax = IRSOptions.find((option) => option.key === maximumElectionAmountIRS);
                    if (foundMax && foundMax.additionalValue) {
                      return (foundMax.additionalValue as number) > _.toNumber(val);
                    }
                  }
                  if (!_.toNumber(maximumElectionAmountCustom) || !val) {
                    if (_.toNumber(maximumElectionAmountCustom) !== 0 && _.toNumber(val) !== 0) {
                      return true;
                    }
                  }
                  return _.toNumber(maximumElectionAmountCustom) > _.toNumber(val);
                },
                message: ELECTION_AMOUNT_TEXT,
              })
              .test({
                test: (val) => {
                  const isErrorLess = !errorFields?.minimumElectionAmountCustom?.value;
                  if (val) {
                    const formattedValue = val.replace(CENTLESS_KEY, '');
                    const errorValue = `${errorFields?.minimumElectionAmountCustom?.value || ''}`.replace(
                      CENTLESS_KEY,
                      ''
                    );
                    return isErrorLess || formattedValue !== errorValue;
                  }
                  return true;
                },
                message: errorFields?.minimumElectionAmountCustom?.message || 'Invalid field',
              })
              .nullable(),
          },
        ],
      },
      {
        name: 'proofOfExpense',
        type: FieldTypes.Dropdown,
        singleMode: true,
        placeholder: 'Select Proof of expense',
        options: [
          { key: 'RECEIPT_REQUIRED', value: 'Merchant/Provider receipt required' },
          { key: 'NO_RECEIPT_REQUIRED', value: 'No receipt required' },
        ],
        validator: VALIDATOR,
        label: 'Proof of expense (manual claims)',
      } as Field,
      {
        name: 'proofOfExpensesCardType',
        type: FieldTypes.Dropdown,
        singleMode: true,
        placeholder: 'Select Proof of expense',
        options: [
          { key: 'RECEIPT_REQUIRED', value: 'Merchant/Provider receipt required' },
          { key: 'NO_RECEIPT_REQUIRED', value: 'No receipt required' },
        ],
        validator: VALIDATOR,
        label: 'Proof of expense (card claims)',
      } as Field,
      {
        name: 'aiAdjudicationType',
        type: FieldTypes.Radio,
        label: 'AI adjudication',
        options: [
          {
            label: 'Enabled for this plan',
            value: aiAdjudicationType.ENABLED,
          },
          {
            label: 'Disabled for this plan',
            value: aiAdjudicationType.DISABLED,
          },
          {
            label: 'Use organization settings',
            value: aiAdjudicationType.ORG_SETTINGS,
          },
        ],
      },

      // new field
      {
        name: PLAN_SETUP_FIELD.IGNORE_ELECTION_LIMIT,
        type: FieldTypes.Checkbox,
        label: (
          <LabelWithSubfield
            title="Ignore point in time election changes"
            desc="Most recent election amount will be made available for all dates of coverage"
          />
        ),
        options: [
          {
            label: 'Yes',
            value: TRUE_KEY,
          },
          {
            label: 'No',
            value: FALSE_KEY,
          },
        ],
        validator: VALIDATOR,
      },

      ...[
        {
          name: PLAN_SETUP_FIELD.TOTAL_PLAN_LIMIT,
          type: FieldTypes.Checkbox,
          label: 'Spend limits',
          compactSubfields: true,
          options: [
            { label: 'Yes', value: TRUE_KEY },
            { label: 'No', value: FALSE_KEY },
          ],
          validator: VALIDATOR,
          helpText: (
            <Box direction="row" align="center" gap="spacing4">
              <Inscription lineHeight="20px">Total plan limit</Inscription>
              <Hint
                hintAlign="right"
                hintWidth={250}
                hintElement={<InfoOutlineIcon color="iconSecondary" style={{ cursor: 'pointer' }} />}
              >
                <Inscription lineHeight="20px">
                  Limits the total amount that can be spent across the plan within the designated spend
                  period, regardless of individual participants.
                </Inscription>
              </Hint>
            </Box>
          ),
          subFields: [
            {
              name: PLAN_SETUP_FIELD.TOTAL_PLAN_LIMIT_TYPE,
              parentValue: true,
              type: FieldTypes.Radio,
              label: ' ',
              options: [
                {
                  label: 'IRS limit',
                  value: SPEND_LIMIT_IRS,
                },
                {
                  label: 'Custom',
                  value: CUSTOM_KEY,
                  isDisabled: isPlanActive,
                },
              ],
              expandItems: true,
              validator: yup.string().when(PLAN_SETUP_FIELD.TOTAL_PLAN_LIMIT, {
                is: (val: boolean | string) => val === true || val === TRUE_KEY,
                then: (schema) => schema.nullable().required(REQUIRED_TEXT),
                otherwise: () => VALIDATOR,
              }),
              subFields: [
                {
                  name: PLAN_SETUP_FIELD.SPEND_LIMIT_IRS_AMOUNT,
                  parentValue: SPEND_LIMIT_IRS,
                  type: FieldTypes.Dropdown,
                  singleMode: true,
                  placeholder: 'Select IRS limit',
                  compactSubfields: true,
                  options: IRSOptions,
                  validator: yup
                    .string()
                    .when([PLAN_SETUP_FIELD.TOTAL_PLAN_LIMIT, PLAN_SETUP_FIELD.TOTAL_PLAN_LIMIT_TYPE], {
                      is: (totalPlanLimit: boolean | string, limitType: string) =>
                        (totalPlanLimit === true || totalPlanLimit === TRUE_KEY) &&
                        limitType === SPEND_LIMIT_IRS,
                      then: (schema) => VALIDATORS.GET_REQUIRED_SCHEMA(schema),
                      otherwise: () => VALIDATORS.STRING,
                    }),
                  label: ' ',
                },
                {
                  name: PLAN_SETUP_FIELD.SPEND_LIMIT_CUSTOM,
                  parentValue: CUSTOM_KEY,
                  type: FieldTypes.Currency,
                  value: '',
                  label: ' ',
                  placeholder: 'Enter $ amount',
                  validator: yup
                    .string()
                    .when([PLAN_SETUP_FIELD.TOTAL_PLAN_LIMIT, PLAN_SETUP_FIELD.TOTAL_PLAN_LIMIT_TYPE], {
                      is: (totalPlanLimit: boolean | string, limitType: string) =>
                        (totalPlanLimit === true || totalPlanLimit === TRUE_KEY) && limitType === CUSTOM_KEY,
                      then: (schema) =>
                        VALIDATORS.GET_REQUIRED_SCHEMA(schema).test({
                          test: (val) => !val || parseCost(val) > 0,
                          message: MUST_BE_GREATER_THAN_ZERO_TEXT,
                        }),
                      otherwise: (schema) => schema,
                    }),
                },
              ],
            },
          ],
        } as Field,
        {
          name: PLAN_SETUP_FIELD.PER_PERSON_SPEND_LIMIT,
          type: FieldTypes.Checkbox,
          compactSubfields: true,
          label: ' ',
          options: [
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
          ],
          validator: VALIDATOR,
          helpText: (
            <Box direction="row" align="center" gap="spacing4">
              <Inscription lineHeight="20px">Person spend limit</Inscription>
              <Hint
                hintAlign="right"
                hintWidth={250}
                hintElement={<InfoOutlineIcon color="iconSecondary" style={{ cursor: 'pointer' }} />}
              >
                <Inscription lineHeight="20px">
                  Limits the amount that can be spent per person within the plan during the designated spend
                  period.
                </Inscription>
              </Hint>
            </Box>
          ),
          subFields: [
            {
              name: PLAN_SETUP_FIELD.PER_PERSON_SPEND_LIMIT_AMOUNT,
              label: ' ',
              parentValue: true,
              type: FieldTypes.Currency,
              placeholder: 'Enter $ amount',
              validator: yup
                .string()
                .trim()
                .when(PLAN_SETUP_FIELD.PER_PERSON_SPEND_LIMIT, {
                  is: (val: boolean | string) => val === true || val === TRUE_KEY,
                  then: (schema) =>
                    schema.required(REQUIRED_TEXT).test({
                      test: (val) => !val || parseCost(val) > 0,
                      message: MUST_BE_GREATER_THAN_ZERO_TEXT,
                    }),
                  otherwise: () => VALIDATOR,
                }),
            },
          ],
        } as Field,
        {
          name: PLAN_SETUP_FIELD.PER_CATEGORY_SPEND_LIMIT,
          type: FieldTypes.Checkbox,
          label: ' ',
          compactSubfields: true,
          options: [
            { label: 'Yes', value: TRUE_KEY },
            { label: 'No', value: FALSE_KEY },
          ],
          validator: VALIDATOR,
          helpText: (
            <Box direction="row" align="center" gap="spacing4">
              <Inscription lineHeight="20px">Service category spend limits</Inscription>
              <Hint
                hintAlign="right"
                hintWidth={250}
                hintElement={<InfoOutlineIcon color="iconSecondary" style={{ cursor: 'pointer' }} />}
              >
                <Inscription lineHeight="20px">
                  Limits the amount that can be spent per service category during the designated spend period.
                </Inscription>
              </Hint>
            </Box>
          ),
          subFields: [
            {
              name: 'perCategorySpendLimitInfo',
              parentValue: true,
              compactSubfields: true,
              type: FieldTypes.Node,
              label: ' ',
              value: (
                <Text color="textSecondary">
                  Per category spend limits can be set in the{' '}
                  <Text color="textAccent" style={{ cursor: 'pointer' }} onClick={handleOpenEligibleServices}>
                    eligible services
                  </Text>{' '}
                  section.
                </Text>
              ),
            },
          ],
        } as Field,
      ],

      ...(isAnySpendLimitSelected
        ? [
            {
              name: PLAN_SETUP_FIELD.SPEND_PERIOD,
              type: FieldTypes.Radio,
              label: 'Spend period',
              options: [
                {
                  label: 'Unlimited',
                  value: 'UNLIMITED',
                },
                {
                  label: 'Monthly',
                  value: 'MONTHLY',
                },
                {
                  label: 'Plan year',
                  value: 'PLAN_YEAR',
                },
              ],
              validator: VALIDATOR,
            } as Field,
          ]
        : []),
      {
        name: 'isOrphaned',
        type: FieldTypes.Radio,
        label: 'Is orphaned',
        showRequireIcon: true,
        options: [
          {
            label: 'Yes',
            value: 'true',
          },
          {
            label: 'No',
            value: 'false',
          },
        ],
        validator: onlyActivationRequired,
      },
      //
      {
        name: 'catchUpElections',
        showRequireIcon: isAllowInvestments,
        parentValue: 'true',
        type: FieldTypes.Currency,
        label: 'Catch-Up elections',
        placeholder: 'Enter $ amount',
        validator: yup
          .string()
          .trim()
          .when(ALLOW_INVESTMENTS_KEY, {
            is: (val: string) => val === TRUE_KEY,
            then: (schema) =>
              VALIDATORS.GET_REQUIRED_SCHEMA(schema).test({
                test: (val) => parseCost(val) !== 0,
                message: GREATER_TEXT,
              }),
            otherwise: () => VALIDATOR,
          }),
      },
      {
        name: 'catchUpAge',
        showRequireIcon: isAllowInvestments,
        parentValue: 'true',
        type: FieldTypes.Number,
        label: 'Catch-Up age',
        placeholder: 'Enter age',
        validator: yup
          .string()
          .trim()
          .when(ALLOW_INVESTMENTS_KEY, {
            is: (val: string) => val === TRUE_KEY,
            then: (schema) =>
              VALIDATORS.GET_REQUIRED_SCHEMA(schema).test({
                test: (val) => parseCost(val) !== 0,
                message: GREATER_TEXT,
              }),
            otherwise: () => VALIDATOR,
          }),
      },
      {
        name: ALLOW_INVESTMENTS_KEY,
        type: FieldTypes.Radio,
        label: 'Allow investments',
        options: [
          {
            label: 'Yes',
            value: TRUE_KEY,
          },
          {
            label: 'No',
            value: 'false',
          },
        ],
        validator: yup.string().test({
          test(val) {
            if (val === BOOLEAN_STRINGS.TRUE && !isHSAOmnibus) {
              return false;
            }
            return true;
          },
          message: 'Make omnibus HSA to support investments',
        }),
        subFields: [
          {
            name: 'investmentOptionsType',
            showRequireIcon: true,
            parentValue: 'true',
            type: FieldTypes.CheckBoxGroup,
            label: 'Investment options',
            placeholder: ' ',
            defaultValue: [],
            options: [
              { key: 'MANAGED', value: 'Managed portfolio' },
              { key: 'SELF_DIRECTED', value: 'Self directed' },
              // { key: 'BROKERAGE', value: 'Brokerage' } // Hide BROKERAGE - EL-19374
            ],
            validator: yup
              .array()
              .nullable()
              .when(ALLOW_INVESTMENTS_KEY, {
                is: (val: string) => val === TRUE_KEY,
                then: () => yup.array().nullable().min(1, REQUIRED_TEXT).required(REQUIRED_TEXT),
                otherwise: () => yup.array().nullable(),
              }),
          },
          {
            name: 'investmentThreshold',
            showRequireIcon: true,
            parentValue: 'true',
            type: FieldTypes.Currency,
            label: 'Investment threshold',
            placeholder: 'Enter $ amount',
            validator: yup
              .string()
              .trim()
              .when(ALLOW_INVESTMENTS_KEY, {
                is: (val: string) => val === TRUE_KEY,
                then: () => VALIDATORS.REQUIRED_POSITIVE_NUMBER_OR_ZERO,
                otherwise: () => VALIDATOR,
              }),
          },
          {
            name: 'minimumInvestmentAmount',
            showRequireIcon: true,
            parentValue: 'true',
            type: FieldTypes.Currency,
            label: 'Minimum investment amount',
            placeholder: 'Enter $ amount',
            validator: yup
              .string()
              .trim()
              .when(ALLOW_INVESTMENTS_KEY, {
                is: (val: string) => val === TRUE_KEY,
                then: () =>
                  yup
                    .string()
                    .required(REQUIRED_TEXT)
                    .test({
                      test: (val) => parseCost(val) !== 0,
                      message: GREATER_TEXT,
                    }),
                otherwise: () => VALIDATOR,
              }),
          },
        ],
      },
      {
        name: 'CIPRequired',
        type: FieldTypes.Checkbox,
        label: 'CIP required',
        options: [
          {
            label: 'Yes',
            value: TRUE_KEY,
          },
          {
            label: 'No',
            value: 'false',
          },
        ],
        validator: VALIDATOR,
        subFields: isDisplayedPendContribution
          ? [
              {
                name: 'CIPAccountClosureDelayDays',
                type: FieldTypes.Number,

                label: (
                  <LabelWithSubfield
                    requiredIcon
                    title="Close unresolved CIP accounts"
                    desc="Number of days an account will be in a failed or pending CIP status prior to closing and returning any pending funds to the participant"
                  />
                ),
                defaultValue: 90,
                validator: REQUIRED_VALIDATOR,
              },
              {
                name: PLAN_SETUP_FIELD.RETURN_FUNDS_AFTER_CIP_ACCOUNT_CLOSURE_TYPE,
                type: FieldTypes.Radio,

                label: 'Return funds after closure',
                options: [
                  {
                    label: 'Employee',
                    value: ReturnFundsAfterClosureType.EMPLOYEE,
                  },
                  {
                    label: 'Employer',
                    value: ReturnFundsAfterClosureType.EMPLOYER,
                  },
                ],
                validator: VALIDATOR,
              },
            ]
          : [],
      },

      isDisplayedPendContribution
        ? {
            name: 'pendContributionsForCIP',
            type: FieldTypes.Checkbox,
            label: 'Pend contributions for CIP',
            options: [
              {
                label: 'Yes',
                value: 'true',
              },
              {
                label: 'No',
                value: 'false',
              },
            ],
            helpText: 'Yes',
            validator: VALIDATOR,
          }
        : {
            name: 'empty',
            type: FieldTypes.Node,
          },
      {
        name: 'CMSReportingRequired',
        type: FieldTypes.Radio,
        label: 'CMS reporting required',
        showRequireIcon: true,
        options: [
          {
            label: 'Yes',
            value: 'true',
          },
          {
            label: 'No',
            value: 'false',
          },
        ],
        validator: onlyActivationRequired,
      },
      {
        name: 'generateStatements',
        type: FieldTypes.Radio,
        label: 'Generate statements',
        showRequireIcon: true,
        options: [
          {
            label: 'Yes',
            value: 'yes',
          },
          {
            label: 'No',
            value: 'no',
          },
        ],
        validator: onlyActivationRequired,
      },
      {
        name: 'includeToBalanceReminder',
        type: FieldTypes.Radio,
        label: 'Include in balance notifications',
        options: [
          {
            label: 'Yes',
            value: 'yes',
          },
          {
            label: 'No',
            value: 'no',
          },
        ],
        validator: VALIDATOR,
      },
    ],
    [
      IRSOptions,
      errorFields,
      isDisplayedPendContribution,
      isActivationMode,
      isHSAOmnibus,
      isReplenished,
      onlyActivationRequired,
      isPlanActive,
      isAllowInvestments,
      isAnySpendLimitSelected,
      handleOpenEligibleServices,
    ]
  );

  // Normalize perPersonSpendLimit from string ('yes'/'no') to boolean for Checkbox compatibility.
  const normalizedSourceState = useMemo(() => {
    if (!appliedSourceState) return appliedSourceState;
    const { perPersonSpendLimit, ...rest } = appliedSourceState;
    const rawValue = perPersonSpendLimit as string | boolean;
    return {
      ...rest,
      perPersonSpendLimit: rawValue === YES_KEY ? true : rawValue === NO_KEY ? false : rawValue,
    };
  }, [appliedSourceState]);

  const filteredField = usePlanFieldsState(fields);
  const formatedFields = useFieldsWithDefaultValues(filteredField, normalizedSourceState);
  const stateFields = useTemplateFieldState(formatedFields, isTemplateMode, isEditMode);
  return stateFields;
};
